/**
 * /dashly-vs-olark/ and /dashly-vs-tawk/ — content.
 *
 * The two exports are the same page with the competitor's name swapped and one
 * heading changed ("better customer service" against "better customer
 * engagement"), so the copy they share lives here once.
 *
 * These are the two best keywords left in the whole migration, and both are
 * open: "olark alternative" is 150/mo US, 200 global at KD 0, and
 * "tawk.to alternatives" is 100/200, also KD 0.
 *
 * Two things in the exports are not carried over.
 *
 * The first is the price comparison. Olark's page put "$95 Olark for 5 agents"
 * against "$59 Dashly", and the Tawk page "$0 + $100+" against the same $59.
 * Every number there is stale: Dashly's cheapest plan on /pricing/ is not $59
 * any more, and a rival's price nobody here has rechecked is worse still.
 * Nothing replaces it. A capability table would have to assert things about
 * Olark and Tawk.to that we have not verified either, so the page makes its
 * case on what Dashly does and sends the price question to /pricing/.
 *
 * The second is the structure. Each export used four separate h1 elements for
 * its four feature sections. They are h2s here, under one h1.
 *
 * The "More about lead collection" link pointed at href="#" in both exports —
 * a dead link — so it is dropped rather than pointed somewhere invented.
 *
 * Four other links in the exports resolved only through a 301: the three case
 * studies sat under /blog/ and the support link under /live-chat-dashly/. They
 * point at the canonical URLs here, so a reader and a crawler get the page
 * without the extra hop.
 *
 * Product-KB check: the claim "Support team is online 24/7" is not in the
 * knowledge base and is not carried into any heading; it stays as the page's
 * own line. Viber is absent from these pages, which is correct — the glossary
 * forbids it.
 */

import type { RichText } from "@shared/arrays/page-common";

export const REGISTER_URL = "/panel/unauthorized/register/";

export interface FeatureBlock {
  /** Written for search; the export's own headline follows as `lead`. */
  title: string;
  lead: string;
  items: Array<{ title: string; text: string }>;
  link?: { label: string; href: string };
}

export const heroShared = {
  quote:
    "“Never turn a prospect away. Dashly captures the information you need to follow-up later”",
  note: "7 days trial, easy setup, no card required",
  cta: "Start for free",
  // hero.avif in the export is a pair of decorative blobs, not artwork; the
  // illustration the hero actually shows is the second image in that block.
  image: "/dashly-vs/hero-2.avif",
  imageAlt: "A support agent answering a visitor in the Dashly chat",
};

/** The four feature sections, each an h1 in the export. */
export const featuresShared: FeatureBlock[] = [
  {
    title: "A live chat your visitors will actually use",
    lead: "The live chat that your visitors will like",
    items: [
      {
        title: "Modern and fun chat experience",
        text: "Forget dated chat windows. Let visitors chat with you like they would with their friends.",
      },
      {
        title: "Don't limit yourself to plain text",
        text: "Send rich messages with images, videos, and buttons to encourage action.",
      },
      {
        title: "Send the right messages to the right visitor",
        text: "Use the user data that Dashly collects to start conversations with the right people on your website.",
      },
    ],
  },
  {
    title: "Never show visitors an offline sign",
    lead: "Dashly is never offline",
    items: [
      {
        title: "Don't give visitors a dead end",
        text: "Instead of turning chat off, let visitors ask questions when and where they have them.",
      },
      {
        title: "Add a self-service option",
        text: "Do not leave a boring reason why you are not online. Create a warm welcome message instead with the link to knowledge base.",
      },
      {
        title: "Capture emails to follow-up",
        text: "Prompt visitors for their email address to send your reply to their inbox if they leave before you respond.",
      },
      {
        title: "Don't restrict conversations to “sessions”",
        text: "Don't hang up on visitors. When people return to your site show their past conversations and let them pick up where they left off.",
      },
    ],
  },
  {
    title: "Everything a support team needs in one inbox",
    lead: "It is for better customer service",
    items: [
      {
        title: "Live chat in your product",
        text: "Respond instantly to customer messages to give them the best experience ever.",
      },
      {
        title: "Inbox for support team",
        text: "One centralized place for messages from all your leads and customer channels. Receive and reply to all communications from the Inbox",
      },
      {
        title: "Knowledge base",
        text: "Create articles about the most important settings in your product and publish them with Dashly Knowledge base",
      },
      {
        title: "Team performance report",
        text: "Monitor the most important metrics of your support team like the response time, team workload, the number of questions solved and so on. Improve your support team based on data.",
      },
    ],
    link: { label: "More about support solution", href: "/live-chat/" },
  },
  {
    title: "More than a simple live chat",
    lead: "More than a simple live chat",
    items: [
      {
        title: "In-app messages",
        text: "Use in-app messaging to help users with any difficulties they may have, so that they get the maximum value out of your product",
      },
      {
        title: "Email campaigns",
        text: "Follow up conversations with your users via email. Tell them about cool updates and valuable content, and stay top of mind",
      },
      {
        title: "User Data",
        text: "See the whole user journey inside your product to know when the trial is over or the subscription is canceled to react quickly and retain users.",
      },
      {
        title: "Analytics",
        text: "See how users activate in your product and use new features. Funnels will show you conversion rates on every step of user journey.",
      },
    ],
  },
];

export const casesShared = {
  title: "What teams got after switching",
  lead: "Success of our customers",
  linkLabel: "Read the success story",
  items: [
    {
      metric: "+25%",
      unit: "new signups",
      text: "How to bring more visitors to sign up? Dashly and IQ Option answered this question and got 25% more sign ups. The story of ups and downs, different A/B tests and a happy end for trade stocks and digital options platform.",
      href: "/pop-ups/how-to-bring-more-sing-ups/",
    },
    {
      metric: "3X",
      unit: "leads within 6 months",
      text: "Dashly and Findthat.email know the answer. The story of how the implementation of one product brought 3 times more leads with one pop-up and a live chat.",
      href: "/pop-ups/case-findthat-email/",
    },
    {
      metric: "+55%",
      unit: "sales",
      text: "Dashly and Kflwebdesign made a cool project for a New York flower shop that brought more leads, improved customer service and helped the flower shop to get 55% more online sales.",
      href: "/pop-ups/increase-online-sales/",
    },
  ],
};

export const mobileShared = {
  title: "Answer from your phone",
  lead: "Talk to users on the go. Use the Dashly mobile apps",
  text: "The mobile app has no limits: share articles, use canned responses, get to know who you're talking to, and leave notes for your teammates.",
  images: [
    { src: "/dashly-vs/mobile-2.avif", alt: "A conversation in the Dashly mobile app" },
    { src: "/dashly-vs/mobile-3.avif", alt: "The inbox in the Dashly mobile app" },
  ],
  badges: [
    { src: "/dashly-vs/badge-apple.avif", alt: "Download on the App Store" },
    { src: "/dashly-vs/badge-google.avif", alt: "Get it on Google Play" },
  ],
};

export const closingShared = {
  lead: "People want personal support instead of bot's small talk",
  supportLine: "Support team is online 24/7",
  supportIcons: [
    "/dashly-vs/closing-1.avif",
    "/dashly-vs/closing-2.avif",
  ],
  title: "Try Dashly today",
  text: "Start from the basic customer communication for free",
  cta: "Start for free",
  note: "7 days trial, easy setup, no card required",
};

/** Each page's own hero copy and comparison rows. */
export const olarkData = {
  lead: "Looking for an alternative to Olark? Dashly is a good one!",
  rival: "Olark",
};

export const tawkData = {
  lead: "Looking for an alternative to Tawk.to? Dashly is a good one!",
  rival: "Tawk.to",
  /** The one heading the two exports did not share. */
  thirdSectionLead: "It is for better customer engagement",
};
